// The app's menu (29 Oct 2026, docs/specs/NAVIGATION_2026_10.md; 30 Oct 2026, docs/specs/CAMPAIGN_LED_2026_10.md): six
// destinations named for what the owner does (Ask Ogma, Campaigns, Conversations, Quotes, Contacts, Results) and Settings
// at the foot. Each destination's pages come
// from its registry in src/modules (one file each). From them this file works out the paths, labels and step ids, the
// breadcrumbs, the waiting counts, where each kind of task shows, and where every old address now opens (MOVED: links
// in emails, tasks, alerts and bookmarks keep working).
import { MODULE_DEFS } from "../modules";
import { pagePath, stepPath, type ModuleDef } from "../modules/types";

export interface NavItem {
  path: string;
  label: string;
  /** The process step or phase the page belongs to, when there is one. */
  step?: string;
  /** Shown in the side list only with the tab's Advanced switch on (ModulePage.advanced). */
  advanced?: boolean;
}

/** One tab across the top of a destination, with its pages down the side. */
export interface NavStep {
  key: string;
  label: string;
  /** `/prospects/drafts`: the tab's address. */
  path: string;
  items: NavItem[];
}

export interface NavSection {
  key: string;
  label: string;
  /** Every page in the menu. For a destination with tabs, all its tabs' pages in order. */
  items: NavItem[];
  /** Set for a destination with tabs (tabs across the top, pages down the side). */
  steps?: NavStep[];
  /** A module switched off for now (ModuleDef.paused): not in the menu; every address under it says Coming soon. */
  paused?: string;
  /** The set-up part that puts the destination in the menu (ModuleDef.shownWithPart). */
  shownWithPart?: string;
}

const navItem = (path: string, label: string, step?: string, advanced?: boolean): NavItem => ({
  path,
  label,
  ...(step ? { step } : {}),
  ...(advanced ? { advanced } : {}),
});

/** A destination's registry as a menu section. */
export function moduleSection(m: ModuleDef): NavSection {
  const extra = { ...(m.paused ? { paused: m.paused } : {}), ...(m.shownWithPart ? { shownWithPart: m.shownWithPart } : {}) };
  if (m.layout === "flat") return { key: m.key, label: m.label, items: m.items.map((i) => navItem(i.path, i.label, i.step)), ...extra };
  const steps: NavStep[] = m.tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    path: stepPath(m.key, tab.key),
    items: tab.pages.map((p) => navItem(pagePath(m.key, tab.key, p.slug), p.label, p.step, p.advanced)),
  }));
  return { key: m.key, label: m.label, items: steps.flatMap((s) => s.items), steps, ...extra };
}

export const SECTIONS: NavSection[] = MODULE_DEFS.map(moduleSection);

/** The destinations in the left menu, in order (paused modules are not). Settings sits at the foot. */
export const MENU_SECTIONS: NavSection[] = SECTIONS.filter((s) => !s.paused);

export const ALL_ITEMS = SECTIONS.flatMap((s) => s.items);

/** Pages reachable but not in the menu (detail pages, Ask, guided set-up): each sits under its destination. */
export const EXTRA_PAGES: { path: string; label: string; section: string }[] = MODULE_DEFS.flatMap((m) =>
  (m.extraPages ?? []).map((p) => ({ path: p.path, label: p.label, section: m.key })),
);

/**
 * Task kinds deliberately shown on Ask Ogma only, with no page of their own. Empty: every kind the app knows has a
 * home (lib/nav.test.ts checks this). Add a kind here only on purpose, with the reason.
 */
export const LATEST_UPDATES_ONLY: string[] = [];

function joinHomes(lists: Record<string, string[]>[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const list of lists)
    for (const [kind, homes] of Object.entries(list)) {
      const into = (out[kind] ??= []);
      for (const h of homes) if (!into.includes(h)) into.push(h);
    }
  return out;
}

/**
 * Where each kind of task waiting for a person shows: the destinations' pages in menu order. The first is where it
 * counts in the menu (lib/attention.ts).
 */
export const TASK_HOMES: Record<string, string[]> = joinHomes(MODULE_DEFS.map((m) => m.taskHomes));

/** The kinds of task a page shows in its own queue. */
export function kindsFor(path: string): string[] {
  return Object.entries(TASK_HOMES)
    .filter(([, homes]) => homes.includes(path))
    .map(([kind]) => kind);
}

/** Where the app opens: Ask Ogma, with what needs you as cards (Craig, 30 Oct 2026). */
export const HOME_PATH = "/ask";

export interface Located {
  section: NavSection;
  /** The tab, for a page of a destination with tabs. */
  step?: NavStep;
  label: string;
  path: string;
  /** The page is one of its tab's advanced pages. */
  advanced?: boolean;
}

/**
 * The destination (and tab) and page a path belongs to. A detail page belongs to its list; where two pages could
 * hold the path (`/quotes` and `/quotes/flows`), the longer one does.
 */
export function locate(pathname: string): Located | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  const under = (path: string) => p === path || p.startsWith(`${path}/`);
  let best: Located | null = null;
  const consider = (found: Located) => {
    if (!best || found.path.length > best.path.length) best = found;
  };
  for (const s of SECTIONS) {
    for (const st of s.steps ?? [])
      for (const i of st.items) if (under(i.path)) consider({ section: s, step: st, label: i.label, path: i.path, ...(i.advanced ? { advanced: true } : {}) });
    if (!s.steps) for (const i of s.items) if (under(i.path)) consider({ section: s, label: i.label, path: i.path });
  }
  if (best) return best;
  // A detail page or Ask: its own address, under its destination (a tab's detail page keeps its tab).
  const extra = EXTRA_PAGES.filter((e) => matchesPattern(e.path, p)).sort((a, b) => b.path.length - a.path.length)[0];
  if (!extra) return null;
  const section = SECTIONS.find((s) => s.key === extra.section)!;
  const step = section.steps?.find((st) => p.startsWith(`${st.path}/`));
  return { section, ...(step ? { step } : {}), label: extra.label, path: extra.path };
}

/** Whether an address fits a route pattern (`/contacts/people/:id`). */
function matchesPattern(pattern: string, path: string): boolean {
  const a = pattern.split("/");
  const b = path.split("/");
  return a.length === b.length && a.every((seg, i) => seg.startsWith(":") || seg === b[i]);
}

/** Where a destination's entry in the left menu leads: its first tab's first page, or its first page. */
export function sectionHome(section: NavSection): string {
  if (section.steps) return section.steps[0]!.items[0]!.path;
  return section.items[0]!.path;
}

/** "Prospects · Drafts · Send yourself": the breadcrumb at the top of each page. */
export function crumbOf(pathname: string): string | null {
  const at = locate(pathname);
  if (!at) return null;
  const parts = [at.section.label, at.step?.label, at.label].filter((x): x is string => Boolean(x));
  return parts.filter((x, i) => x !== parts[i - 1]).join(" · ");
}

/**
 * Every old address and where it opens now (29 Oct 2026 and earlier moves), for links in emails, alerts, tasks made
 * before a move and bookmarks. Parameters (:id) carry over; the query string and the #section always do. Each becomes a
 * redirect route, and newAddress() maps a task's or note's `to` with the same table.
 */
export const MOVED: [string, string][] = [
  // 31 Oct 2026: Ask the CRM became part of Ask Ogma; a table answer opens in its side window (?q= starts the chat)
  ["/ask/table", "/ask"],
  // 30 Oct 2026: What's running and Engine checks became Settings › Automations
  ["/settings/account/system", "/settings/automations"],
  ["/settings/account/checks", "/settings/automations"],
  // 30 Oct 2026 (docs/specs/CAMPAIGN_LED_2026_10.md): Home became Ask Ogma; Prospects became Campaigns, with the search
  // in Contacts › Find; Campaign defaults and the routes after a reply moved into Campaigns.
  ["/home", "/ask"],
  ["/prospects", "/campaigns/list"],
  ["/prospects/search", "/contacts/find"],
  ["/prospects/lined-up", "/campaigns/lined-up"],
  ["/prospects/lined-up/add", "/contacts/find/firm"],
  ["/prospects/lined-up/news", "/contacts/find/news"],
  ["/prospects/campaigns", "/campaigns/list"],
  ["/prospects/drafts", "/campaigns/to-approve"],
  ["/prospects/drafts/yourself", "/campaigns/to-approve"],
  ["/prospects/drafts/scheduled", "/campaigns/outreach"],
  ["/prospects/drafts/week", "/campaigns/outreach"],
  // Campaign pages redesign (31 Oct 2026): To approve's sub-pages, and Routes now each campaign's own steps
  ["/campaigns/to-approve/yourself", "/campaigns/to-approve"],
  ["/campaigns/to-approve/scheduled", "/campaigns/outreach"],
  ["/campaigns/to-approve/week", "/campaigns/outreach"],
  ["/campaigns/shared", "/settings/business/paths"],
  ["/settings/campaign-defaults", "/campaigns/defaults"],
  ["/settings/campaign-defaults/speed", "/campaigns/defaults/speed"],
  ["/settings/campaign-defaults/save-offer", "/campaigns/defaults/save-offer"],
  ["/settings/campaign-defaults/documents", "/campaigns/defaults/documents"],
  ["/conversations/routes", "/settings/business/paths"],
  // The engine town (removed for now) opens Ask Ogma.
  ["/control", "/ask"],
  ["/control/engine", "/ask"],
  ["/control/updates", "/ask"],
  ["/control/running", "/settings/automations"],
  ["/engine", "/ask"],
  ["/welcome", "/ask"],
  ["/approvals", "/ask"],
  ["/approvals/*", "/ask"],
  ["/process", "/settings/business/playbook?tab=process"],
  // The CRM is Contacts; its tools moved to Settings › Data.
  ["/crm", "/contacts/companies"],
  ["/contacts", "/contacts/companies"],
  ["/crm/companies", "/contacts/companies"],
  ["/crm/companies/:id", "/contacts/companies/:id"],
  ["/companies", "/contacts/companies"],
  ["/firms", "/contacts/companies"],
  ["/firms/:id", "/contacts/companies/:id"],
  ["/firm/:id", "/contacts/companies/:id"],
  ["/crm/people", "/contacts/people"],
  ["/crm/people/:id", "/contacts/people/:id"],
  ["/people", "/contacts/people"],
  ["/people/:id", "/contacts/people/:id"],
  ["/person/:id", "/contacts/people/:id"],
  ["/crm/contacts", "/settings/data"],
  ["/crm/duplicates", "/settings/data/duplicates"],
  ["/crm/settings", "/settings/data/crm"],
  ["/crm/deals", "/conversations/deals"],
  ["/deals", "/conversations/deals"],
  // Settings and the old Settings pages are Settings' sections now.
  ["/company", "/settings/business"],
  ["/company/offer", "/settings/business"],
  ["/company/brand", "/settings/business/brand"],
  ["/settings/brand", "/settings/business/brand"],
  ["/company/playbook", "/settings/business/playbook"],
  ["/playbook", "/settings/business/playbook"],
  ["/company/plays", "/settings/business/plays"],
  ["/company/modules", "/settings/modules"],
  ["/settings/your-settings", "/settings/modules"],
  ["/company/accounts", "/settings/connections"],
  ["/company/team", "/settings/account/team"],
  ["/team", "/settings/account/team"],
  ["/company/exports", "/settings/data/exports"],
  ["/exports", "/settings/data/exports"],
  ["/company/approvals", "/settings/approvals"],
  ["/autonomy", "/settings/approvals"],
  ["/settings/profile", "/settings/account"],
  ["/settings/general", "/settings/account"],
  ["/settings/personal", "/settings/account/personal"],
  ["/settings/danger", "/settings/account/danger"],
  ["/settings/engine", "/settings/automations"],
  ["/settings/speed-to-lead", "/campaigns/defaults/speed"],
  // Find: its working pages are Campaigns and Contacts › Find, its accounts and rules Settings, its figures Results.
  ["/find", "/campaigns/list"],
  ["/find/accounts", "/settings/sending"],
  ["/find/accounts/mailboxes", "/settings/sending"],
  ["/find/accounts/data-sources", "/settings/data/sources"],
  ["/find/accounts/crm-link", "/settings/data/crm-link"],
  ["/find/accounts/send-more", "/settings/sending/more"],
  ["/find/accounts/phone", "/settings/modules/phone"],
  ["/find/accounts/letters", "/settings/modules/letters"],
  ["/find/accounts/whatsapp", "/settings/modules/whatsapp-outreach"],
  ["/find/accounts/linkedin", "/settings/modules/linkedin"],
  ["/find/accounts/contacts", "/settings/data"],
  ["/find/contacts", "/settings/data"],
  ["/find/rules", "/settings/sending/times"],
  ["/find/rules/sending", "/settings/sending/times"],
  ["/find/sending", "/settings/sending/times"],
  ["/find/rules/style", "/campaigns/defaults"],
  ["/find/rules/campaigns", "/campaigns/list"],
  ["/find/rules/targeting", "/campaigns/list"],
  ["/find/rules/offer", "/campaigns/list"],
  ["/find/offer", "/campaigns/list"],
  ["/find/rules/approvals", "/settings/approvals/outreach"],
  ["/find/research", "/contacts/find"],
  ["/find/research/prospects", "/contacts/find"],
  ["/find/sourcing", "/contacts/find"],
  ["/find/research/long-list", "/campaigns/lined-up"],
  ["/find/research/add-target", "/contacts/find/firm"],
  ["/find/research/signals", "/contacts/find/news"],
  ["/find/news", "/contacts/find/news"],
  ["/find/actions", "/campaigns/to-approve"],
  ["/find/actions/overview", "/ask"],
  ["/find/actions/drafts", "/campaigns/to-approve"],
  ["/find/actions/letters", "/campaigns/to-approve"],
  ["/find/actions/by-hand", "/campaigns/to-approve"],
  ["/find/actions/linkedin", "/campaigns/to-approve"],
  ["/find/actions/sending", "/campaigns/outreach"],
  ["/find/actions/week", "/campaigns/outreach"],
  ["/find/actions/calls", "/settings/modules/calls"],
  ["/find/calls", "/settings/modules/calls"],
  ["/find/actions/campaigns", "/reconnect/actions/bulk-emails"],
  ["/find/performance", "/results/outreach"],
  ["/find/performance/results", "/results/outreach"],
  ["/find/results", "/results/outreach"],
  ["/find/performance/advice", "/results/outreach/advice"],
  ["/find/performance/time", "/results/time"],
  ["/find/performance/health", "/settings/sending/health"],
  ["/find/events", "/network/research/events"],
  ["/find/referrers", "/network/research/introducers"],
  // Connect: its working pages are Conversations, its accounts and rules Settings, its figures Results.
  ["/pipeline", "/conversations"],
  ["/pipeline/accounts", "/settings/connections/sales"],
  ["/pipeline/accounts/overview", "/settings/connections/sales"],
  ["/pipeline/accounts/calendar", "/settings/meetings"],
  ["/pipeline/accounts/booking-links", "/settings/meetings/links"],
  ["/pipeline/booking", "/settings/meetings/links"],
  ["/pipeline/accounts/video", "/settings/meetings/video"],
  ["/pipeline/accounts/whatsapp", "/settings/modules/whatsapp"],
  ["/pipeline/accounts/phone-line", "/settings/modules/phone-line"],
  ["/pipeline/accounts/payments", "/settings/connections/payments"],
  ["/pipeline/accounts/documents", "/settings/connections/documents"],
  ["/pipeline/accounts/notetaker", "/settings/meetings/notetaker"],
  // The routes after a reply: shared set-ups in Campaigns (30 Oct 2026).
  ["/settings/campaign-defaults/after-reply", "/settings/business/paths"],
  ["/pipeline/rules", "/settings/business/paths"],
  ["/pipeline/rules/paths", "/settings/business/paths"],
  ["/pipeline/paths", "/settings/business/paths"],
  ["/pipeline/rules/pace", "/settings/business/paths#defaults"],
  ["/pipeline/rules/booking-hours", "/settings/meetings/hours"],
  ["/pipeline/rules/documents", "/campaigns/defaults/documents"],
  ["/pipeline/rules/speed-to-lead", "/campaigns/defaults/speed"],
  ["/pipeline/rules/missed-calls", "/campaigns/defaults/speed#missed-call"],
  ["/pipeline/rules/meeting-types", "/settings/meetings/types"],
  ["/pipeline/rules/save-offer", "/campaigns/defaults/save-offer"],
  ["/pipeline/playbooks", "/campaigns/defaults/save-offer"],
  ["/pipeline/rules/whatsapp", "/settings/modules/whatsapp-templates"],
  ["/pipeline/whatsapp", "/settings/modules/whatsapp-templates"],
  ["/pipeline/rules/approvals", "/settings/approvals/conversations"],
  ["/pipeline/rules/pricing", "/quotes/price-lists"],
  ["/pipeline/pricing", "/quotes/price-lists"],
  ["/pipeline/research", "/conversations/meetings/briefs"],
  ["/pipeline/research/briefs", "/conversations/meetings/briefs"],
  ["/pipeline/research/history", "/conversations/meetings/history"],
  ["/pipeline/actions", "/conversations/next-steps"],
  ["/pipeline/actions/conversations", "/conversations/inbox"],
  ["/pipeline/actions/replies", "/conversations/inbox?tab=replies"],
  ["/pipeline/replies", "/conversations/inbox?tab=replies"],
  ["/pipeline/actions/whatsapp", "/conversations/inbox?tab=whatsapp-closed"],
  ["/pipeline/whatsapp-inbox", "/conversations/inbox?tab=whatsapp-closed"],
  ["/pipeline/actions/next-steps", "/conversations/next-steps"],
  ["/pipeline/actions/sales-actions", "/conversations/next-steps"],
  ["/pipeline/actions/meetings", "/conversations/meetings"],
  ["/pipeline/actions/meetings/:id", "/conversations/meetings/:id"],
  ["/pipeline/meetings", "/conversations/meetings"],
  ["/meetings", "/conversations/meetings"],
  ["/pipeline/actions/deals", "/conversations/deals"],
  ["/pipeline/actions/quotes", "/quotes"],
  ["/pipeline/performance", "/results/overview"],
  ["/pipeline/performance/sales", "/results/overview"],
  ["/performance", "/results/overview"],
  ["/pipeline/performance/pipeline", "/results/sales"],
  ["/pipeline/summary", "/results/sales"],
  ["/pipeline/performance/speed-to-lead", "/results/sales/speed"],
  ["/pipeline/performance/time", "/results/time/conversations"],
  ["/pipeline/performance/payments", "/results/sales/payments"],
  ["/pipeline/performance/health", "/settings/automations"],
  // Quote is Quotes.
  ["/quote", "/quotes"],
  ["/quote/quotes", "/quotes"],
  ["/quote/books", "/quotes/price-lists"],
  ["/quote/flows", "/quotes/flows"],
  ["/quote/knowledge", "/quotes/knowledge"],
  // The paused modules' older addresses (their pages say Coming soon until each comes back).
  ["/attract/routes", "/attract/performance/results"],
  ["/attract/content", "/attract/actions/long-form"],
  ["/content", "/attract/actions/long-form"],
  ["/attract/socials", "/attract/actions/posts"],
  ["/attract/website", "/attract/accounts/website"],
  ["/website/studio", "/attract/actions/website"],
  ["/attract/newsletter", "/attract/actions/newsletter"],
  ["/attract/ads", "/attract/actions/ads"],
  ["/attract/listings", "/attract/actions/listings"],
  ["/attract/ctas", "/attract/rules/ctas"],
  ["/attract/presence", "/attract/accounts/brand"],
  ["/attract/video", "/attract/actions/posts?channel=video"],
  ["/network/referrers", "/network/research/introducers"],
  ["/network/events", "/network/actions/waiting"],
  ["/reconnect/keep-warm", "/reconnect/actions/waiting"],
  ["/reconnect/whatsapp", "/reconnect/actions/whatsapp"],
  ["/reconnect/spoken-before", "/reconnect/research/spoken-before"],
];

/** The redirect routes: every old address, mapped to its new home. */
export const REDIRECTS: [string, string][] = MOVED;

const MOVED_EXACT: Map<string, string> = new Map(MOVED.filter(([from]) => !from.includes(":") && !from.endsWith("*")));

const KIND_ADDRESS: Record<string, Record<string, string>> = {
  post_review_reply: { "/attract/listings": "/network/actions/reviews" },
};

/**
 * Where a `to` address from a task or note should open now: an old address becomes its new page (keeping the query
 * string and #section, and an :id); anything else is returned as it is.
 */
export function newAddress(to: string, kind?: string): string {
  const m = /^([^?#]*)(.*)$/.exec(to)!;
  const path = m[1]!.replace(/\/+$/, "") || "/";
  let home = (kind && KIND_ADDRESS[kind]?.[path]) ?? MOVED_EXACT.get(path);
  if (!home) {
    // An old detail address: /crm/companies/<id> → /contacts/companies/<id>.
    const pattern = MOVED.find(([from]) => from.includes(":") && matchesPattern(from, path));
    if (!pattern) return to;
    const vals = path.split("/");
    home = pattern[1].replace(/:([a-z]+)/gi, (_, k: string) => vals[pattern[0].split("/").indexOf(`:${k}`)] ?? "");
  }
  const rest = m[2]!;
  if (!rest) return home;
  // The new home may carry a query of its own (?channel=video): join the old one onto it.
  if (home.includes("?") && rest.startsWith("?")) return `${home}&${rest.slice(1)}`;
  if (home.includes("#") && rest.startsWith("#")) return home.split("#")[0]! + rest;
  return home + rest;
}

/**
 * The old nine-step set-up wizard is retired: its addresses open the closest step of guided set-up (/setup) or the
 * screen for that part, keeping the query string (the mailbox sign-in comes back to
 * /onboarding/accounts?mailbox=connected). Before going live: ONBOARDING_TO_SETUP. After going live: ONBOARDING_LIVE.
 */
export const ONBOARDING_TO_SETUP: Record<string, string> = {
  intake: "/setup/core",
  brand: "/setup/parts/attract",
  playbook: "/setup",
  domain: "/setup/connect",
  accounts: "/setup/connect",
  channels: "/setup/parts/find",
  website: "/setup/parts/attract",
  settings: "/setup",
  engine: "/setup",
  finish: "/setup/live",
};

/** After going live, each old wizard step's closest screen. */
export const ONBOARDING_LIVE: Record<string, string> = {
  intake: "/setup/core",
  brand: "/settings/business/brand",
  playbook: "/settings/business/plays",
  domain: "/settings/connections",
  accounts: "/settings/connections",
  channels: "/setup/parts/find",
  website: "/setup/parts/attract",
  settings: "/settings/modules",
  engine: "/settings/automations",
  finish: "/ask",
};

export function setupPathForOnboarding(step: string | undefined, onboarded = false): string {
  if (onboarded) return (step && ONBOARDING_LIVE[step]) || "/setup/modules";
  return (step && ONBOARDING_TO_SETUP[step]) || "/setup";
}

/**
 * Where a set-up link should go for this business: an old wizard address becomes its new home, and "your first play"
 * (/setup/play) becomes the Plays page once live.
 */
export function ownerSetupLink(to: string, onboarded: boolean): string {
  const [path, query] = to.split("?") as [string, string | undefined];
  const q = query ? `?${query}` : "";
  const old = /^\/onboarding(?:\/([a-z-]+))?\/?$/.exec(path);
  if (old) return setupPathForOnboarding(old[1], onboarded) + q;
  if (onboarded && path === "/setup/play") return `/settings/business/plays${q}`;
  return to;
}

/** Fill an old path's parameters into its new home. */
export function redirectTarget(to: string, params: Record<string, string | undefined>): string {
  return to.replace(/:([a-z]+)/gi, (_, k: string) => encodeURIComponent(params[k] ?? ""));
}
